<template>
    <div id="time-choose-menu"
        class="time-choose-menu"
    >
        <section class="menu-ctrl">
            <span class="menu-ctrl__text">{{ $t('__time_choose.top_hint') }}</span>
        </section>
        <div id="time-choose"
            class="time-choose"
        >
            <TreeView
                :init_items="init_items"
                :checked_selectors="checked_selectors"
                :init_opened_layers="init_opened_layers"
                :check_function="change_time_status"
                :enable_custom_label="true"
            >
                <template #content="{ item }">
                    <div v-if="item.name === $t('__time_choose.customized_search')"
                        class="history-search history-search__container"
                        :class="{ 'cursor-auto': is_disabled($t('__time_choose.customized_search')) }"
                    >
                        <v-menu
                            v-model="menu_date"
                            :close-on-content-click="false"
                            :nudge-right="40"
                            transition="scale-transition"
                            offset-y
                            min-width="290px"
                        >
                            <template #activator="{ on }">
                                <v-text-field
                                    v-model="date"
                                    class="start-date__text-field"
                                    :label="$t('__time_choose.start_date_label')"
                                    :disabled="is_disabled($t('__time_choose.customized_search'))"
                                    readonly
                                    v-on="on"
                                    color="white"
                                    hide-details
                                />
                            </template>
                            <v-date-picker 
                                v-model="model_date"
                                :locale="language !== 'en'? 'zh-tw' : 'en' "
                                :min="allowed_min_date"
                                :max="allowed_max_date"
                            >
                                <v-spacer />
                                <v-btn
                                    text
                                    color="primary"
                                    @click="cancel_date_pick"
                                >
                                    {{ $t('__time_choose.cancel') }}
                                </v-btn>
                                <v-btn
                                    text
                                    color="primary"
                                    @click="toggle_generate_time_durations(true)"
                                >
                                    {{ $t('__time_choose.decide') }}
                                </v-btn>
                            </v-date-picker>
                        </v-menu>
                        <v-menu
                            ref="menu_time"
                            v-model="menu_time"
                            :close-on-content-click="false"
                            :nudge-right="40"
                            transition="scale-transition"
                            offset-y
                            max-width="290px"
                            min-width="290px"
                        >
                            <template #activator="{ on }">
                                <v-text-field
                                    v-model="time"
                                    class="start-time__text-field"
                                    :label="$t('__time_choose.start_time_label')"
                                    :disabled="is_disabled($t('__time_choose.customized_search'))"
                                    readonly
                                    v-on="on"
                                    color="white"
                                    hide-details
                                />
                            </template>
                            <v-time-picker
                                v-if="menu_time"
                                v-model="model_time"
                                format="24hr"
                                :allowed-minutes="allowed_minutes"
                                :max="allowed_max_time"
                            >
                                <template>
                                    <v-btn
                                        text
                                        color="primary"
                                        @click="back_to_date_picker"
                                    >
                                        <v-icon size="16">
                                            icon-arrow-back
                                        </v-icon>
                                        {{ $t('__time_choose.back_to_date_select') }}
                                    </v-btn>
                                    <v-spacer />
                                    <v-btn
                                        text
                                        color="primary"
                                        @click="cancel_time_pick"
                                    >
                                        {{ $t('__time_choose.cancel') }}
                                    </v-btn>
                                    <v-btn
                                        text
                                        color="primary"
                                        @click="toggle_generate_time_durations()"
                                    >
                                        {{ $t('__time_choose.decide') }}
                                    </v-btn>
                                </template>
                            </v-time-picker>
                        </v-menu>
                        <v-select
                            v-model="selected_time_duration"
                            class="display-internal"
                            :items="time_duration"
                            :label="$t('__time_choose.display_interval_label')"
                            :disabled="is_disabled($t('__time_choose.customized_search'))"
                            @change="change_duration"
                            color="white"
                        />
                    </div>
                    <div v-if="item.name === '個案查詢' && Object.keys(all_cases).length > 0"
                        class="case-search case-search__container"
                        :class="{ 'cursor-auto': is_disabled('個案查詢') }"
                    >
                        <v-select
                            v-model="current_case_year"
                            :items="case_year_list"
                            :disabled="is_disabled('個案查詢')"
                            label="年份"
                            hide-details
                            color="white"
                            @change="generate_single_case(); control_case_selector_dialog(true);"
                        />
                        <div>
                            <v-select
                                v-model="current_case"
                                :items="case_group_by_year"
                                :disabled="is_disabled('個案查詢')"
                                label="個案"
                                hide-details
                                color="white"
                            />
                            <p class="case-search__internal-text">
                                {{ display_case_datetime }}
                            </p>
                        </div>
                        <button 
                            class="case-search__selector-btn"
                            :class="{
                                'case-search__selector-btn--active': time_selected === '個案查詢'
                            }"
                            @click="control_case_selector_dialog(true)"
                        >
                            開啟選取個案介面
                        </button>
                        <v-dialog 
                            content-class="case-search__dialog"
                            v-model="is_show_case_selector"
                        >
                            <CaseSelector
                                @control-case-selector-dialog="control_case_selector_dialog"
                                @enter-case-mode="enter_case_mode"
                                :init_year="current_case_year"
                            />
                        </v-dialog>
                    </div>
                </template>
            </TreeView>
        </div>
    </div>
</template>

<script>
    import { transform_datetime_to_HHMM } from '@/utils/common/custom_time.js';
    import { mapState, mapGetters, mapMutations } from 'vuex';
    import TreeView from '@/components/tree_view.vue';

    export default {
        name: 'TimeChoose',

        components: { 
            TreeView,
            CaseSelector: () => import ('@/components/case/case_selector.vue'),
        },

        props: {
            is_hide_history: {
                type: Boolean,
                default: false
            }
        },
        
        data() {
            return {
                checked_selectors: {
                    time_choose_1: {id: 'time_choose_1', checked: true},
                    time_choose_2: {id: 'time_choose_2', checked: false},
                    time_choose_3: {id: 'time_choose_3', checked: false},
                },
                init_checked_value: '',

                last_time_selected: this.$t('__time_choose.real_time'),
                time_selected: this.$t('__time_choose.real_time'),
                
                date: new Date(+new Date() + 8*3600000).toISOString().substr(0, 10),
                model_date: new Date(+new Date() + 8*3600000).toISOString().substr(0, 10),
                time: null,
                model_time: null,
                menu_date: false,
                menu_time: false,
                
                time_duration: [],
                selected_start_time: '',
                selected_end_time: '',
                selected_time_duration: '',
                allowed_minutes: element => element % 10 === 0,

                current_case_year: '',
                current_case: '',
                case_year_list: [],
                case_group_by_year: [],
                display_case_datetime: '',

                is_show_case_selector: false,
            }
        },

        computed: {
            ...mapState([
                'client_name',
                'language',
            ]),

            ...mapState('emap', [
                'time_choose_parameters'
            ]),
            
            ...mapState('case', [
                'all_cases'
            ]),

            ...mapGetters([
                'is_pub'
            ]),

            init_opened_layers() {
                return [this.$t('__time_choose.node_name')]
            },

            init_items() {
                let basic_children = [
                    {
                        id:"time_choose_1",
                        index:0,
                        layer1:false,
                        name: this.$t('__time_choose.real_time'),
                        node:false,
                        radio_name: this.$t('__time_choose.node_name'),
                        type:"cross_type_single_radio",
                    },
                ]
                if (!this.is_hide_history) {
                    basic_children = [
                        ...basic_children,
                        {
                            id:"time_choose_2",
                            index:1,
                            layer1:false,
                            name: this.$t('__time_choose.customized_search'),
                            node:false,
                            radio_name: this.$t('__time_choose.node_name'),
                            type:"cross_type_single_radio",
                        }
                    ]
                }
                if(!(this.all_cases === null || Object.keys(this.all_cases).length === 0)){
                    basic_children = [
                        ...basic_children,
                        {
                            id:"time_choose_3",
                            index:2,
                            layer1:false,
                            name:"個案查詢",
                            node:false,
                            radio_name: this.$t('__time_choose.node_name'),
                            type:"cross_type_single_radio",
                        }
                    ]
                }

                return [{
                    layer1: true,
                    name: this.$t('__time_choose.node_name'),
                    node: true,
                    radio_name: `${this.$t('__time_choose.node_name')}_root`,
                    children: basic_children,
                }]
            },

            is_disabled() {
                return (name) => this.time_selected !== name
            },

            allowed_min_date() {
                if(this.is_pub) {
                    const LIMIT_DAYS = 3
                    return new Date(+new Date() + (-24*LIMIT_DAYS + 8)*3600000).toISOString().substring(0, 10)
                }
                return ''
            },

            allowed_max_date() {
                return new Date(+new Date() + 8*3600000).toISOString().substring(0, 10)
            },
            
            allowed_max_time() {
                const today_time_ISO_string = new Date(+new Date() + 8*3600000).toISOString()
                if(this.date === today_time_ISO_string.substring(0, 10)) {
                    return today_time_ISO_string.substring(11, 16)
                }
                return ''
            }
        },

        watch: {
            language() {
                this.time && this.generate_time_durations()
            },

            time_list() {
                this.time_selected = this.$t('__time_choose.real_time')
            },

            all_cases: {
                immediate: true,
                handler() {
                    this.set_all_data()
                },
                deep: true
            },

            current_case() {
                this.set_case_datatime()
            }
        },

        created() {
            this.set_all_data()
        },

        methods: {
            ...mapMutations({
                set_time_choose_parameters: 'emap/set_time_choose_parameters',
                set_atlas_loaded: 'atlas/set_atlas_loaded',
                set_monitor_loaded: 'monitor/set_monitor_loaded',
                set_case_selected: 'case/set_case_selected',
            }),

            set_all_data() {
                this.generate_case_year_list()
                this.generate_single_case()
                this.set_case_datatime()
            },

            set_case_datatime() {
                if (!this.all_cases[this.current_case_year]) return this.display_case_datetime = ''

                const choosed_case_index = this.all_cases[this.current_case_year]
                    .findIndex(({ case_name }) => case_name === this.current_case)
                let choosed_case_start_time = new Date(Number(this.all_cases[this.current_case_year][choosed_case_index].start))
                let choosed_case_end_time = new Date(Number(this.all_cases[this.current_case_year][choosed_case_index].end))

                // 先 +8 小時， toISOString 會轉 UTC time 等於 不管在哪裡皆GMT+8
                choosed_case_start_time.setHours(choosed_case_start_time.getHours() + 8)
                choosed_case_end_time.setHours(choosed_case_end_time.getHours() + 8)
                
                this.display_case_datetime = choosed_case_start_time.toISOString().substring(5, 10) + ' ' + choosed_case_start_time.toISOString().substring(11, 16) + ' ~ ' + choosed_case_end_time.toISOString().substring(5, 10) + ' ' + choosed_case_end_time.toISOString().substring(11, 16)    
            },

            change_time_status(id) {
                this.time_selected = this.init_items[0].children.find(item => item.id === id).name
            },

            cancel_date_pick() {
                this.menu_date = false
                this.model_date = this.date
            },

            cancel_time_pick() {
                this.menu_time = false
                this.model_time = this.time
            },

            toggle_generate_time_durations() {
                this.menu_date = false 
                this.menu_time = false 

                this.date = this.model_date
                this.time = this.model_time
                
                // 選完時間才做事
                this.time && this.generate_time_durations()
            },

            generate_time_durations() {
                const get_display_interval_index = () => {
                    if (this.time_duration) {
                        return this.time_duration.findIndex(({ time }) => time === this.selected_end_time)
                    }   
                    return -1
                } 

                // 如果已經選過顯示時段了，要把 index 抓回來繼續用
                const display_interval_index = get_display_interval_index()

                const ISO_str = `${this.date}T${this.time}:00+08:00`
                this.selected_start_time = new Date(ISO_str);
                const start_time_text = transform_datetime_to_HHMM(this.selected_start_time)
                this.time_duration = [...Array(12)].map((item, index) => {
                    const plus_hour = index + 1
                    let selected_end_time = new Date(this.selected_start_time.getTime());
                    selected_end_time.setHours(selected_end_time.getHours() + plus_hour);

                    const end_time_text = transform_datetime_to_HHMM(selected_end_time)                    
                    
                    if (this.language === 'en') {
                        const hour_text = plus_hour === 1 ? 'hour' : 'hours'
                        return {
                            'text': `
                                ${plus_hour} ${hour_text}
                                （${start_time_text} - ${end_time_text}）
                            `,
                            'time': selected_end_time
                        };    
                    }

                    return {
                        'text': `
                            ${plus_hour}小時
                            （${start_time_text} - ${end_time_text}）
                        `,
                        'time': selected_end_time
                    };
                }) 
                
                if (display_interval_index > 0) {
                    this.selected_time_duration = this.time_duration[display_interval_index].text;
                    this.selected_end_time = this.time_duration[display_interval_index].time;
                } else {
                    this.selected_time_duration = this.time_duration[0].text;
                    this.selected_end_time = this.time_duration[0].time;
                }
            },

            back_to_date_picker() {
                this.menu_time = false;
                this.menu_date = true;
            },

            reset_time() {
                this.time = null
                this.time_duration = null
            },

            compute_time_choose_parameters() {
                const status_mapping = {
                    [this.$t('__time_choose.customized_search')]: 'history',
                    '個案查詢': 'case',
                }
                
                const get_time_epoch = (time) => {
                    const is_check_customized_search = this.time_selected === this.$t('__time_choose.customized_search')
                    return is_check_customized_search ? time.getTime() : ''
                }

                const get_case_selected = () => {
                    const is_check_case_search = this.time_selected === '個案查詢'
                    if (!is_check_case_search) return {}

                    return this.all_cases[this.current_case_year]
                        .find(({ case_name }) => case_name === this.current_case)
                }

                const status = status_mapping[this.time_selected] ?? 'newest'
                const start_time_epoch = get_time_epoch(this.selected_start_time);
                const end_time_epoch = get_time_epoch(this.selected_end_time);
                const case_selected = get_case_selected();
                
                return {
                    status,
                    start_time_epoch,
                    end_time_epoch,
                    case_selected
                }
            },

            change_duration() {
                const index = this.time_duration.findIndex(({ text }) => text === this.selected_time_duration)
                this.selected_end_time = this.time_duration[index].time;
            },

            generate_case_year_list() {
                if(!(this.all_cases === null || Object.keys(this.all_cases).length === 0)){
                    this.case_year_list = Object.keys(this.all_cases).sort((a, b) => b - a )
                    if (!this.current_case_year){
                        this.current_case_year = this.case_year_list[0];
                    }
                }
            },

            generate_single_case() {
                if(!(this.all_cases === null || Object.keys(this.all_cases).length === 0)){
                    this.case_group_by_year = this.all_cases[this.current_case_year].map(({case_name}) => case_name)
                    this.current_case = this.case_group_by_year[0]
                }
            },
            
            control_case_selector_dialog(boolean_value) {
                this.is_show_case_selector = boolean_value
            },

            enter_case_mode(case_selected) {
                this.current_case_year = case_selected.start_time.split('-')[0]
                this.generate_single_case()
                this.current_case = case_selected.case_name
                this.control_case_selector_dialog(false)
                this.$emit('trigger-display-function')
            },
        },
    }
</script>

<style lang="scss" scoped>
    @import '../../../../css/mixin';

    .time-choose {
        height: calc(var(--app-height) - 126px);
        background: #454d66;
    }

    .menu-ctrl {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 3px;
        background: var(--base-gray-darken);

        &__text {
            margin-left: 1em;
            color: var(--grey-pipe);
        }
    }

    .history-search {
        &__container {
            display: grid;
            grid-template-columns: 1fr 1fr;
            
            @include desktop-to-laptop {
                grid-template-columns: 1fr;
            }

            @include mobile {
                grid-template-columns: 1fr;
            }

            &::before {
                content: '';
                position: absolute;
                top: 2.5rem;
                left: 37px;
                width: 1px;
                height: 75%;
                background: var(--base-gray-darken);
            }
        }

        .start-date__text-field {
            padding: 1rem 1rem 0 0;

            @include desktop-to-laptop {
                padding: 1rem 0 0 0;
            }
            
            @include mobile {
                padding: 1rem 0 0 0;
            }
        }

        .start-time {
            &__text-field {
                padding: 1rem 0;
            }
        }

        .display-internal {
            padding-top: 0;
            grid-column-start: 1;
            grid-column-end: 3;

            @include desktop-to-laptop {
                grid-column-start: 1;
                grid-column-end: 2;
            }

            @include mobile {
                grid-column-start: 1;
                grid-column-end: 2;
            }
        }
    }

    .case-search {
        &__container {
            display: grid;
            padding-top: .5rem;
            grid-template-columns: 1fr;
            grid-gap: 1rem;

            &::before {
                content: '';
                position: absolute;
                top: 2.5rem;
                left: 37px;
                width: 1px;
                height: 85%;
                background: var(--base-gray-darken);
            }
        }

        &__selector-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 10rem;
            height: 2rem;
            font-size: 16px;
            font-weight: 900;
            letter-spacing: .6px;
            line-height: 1.5;
            color: #000;
            background-color: var(--aqua-active);
            border-radius: 2rem;
            visibility: hidden;

            &--active {
                visibility: visible;
            }
        }

        .start-date {
            &__text-field {
                padding: 1rem 1rem 0 0;
            }
        }

        .start-time {
            &__text-field {
                padding: 1rem 0;
            }
        }

        .display-internal {
            padding-top: .6rem;
            grid-column-start: 1;
            grid-column-end: 3;
        }
    }

    @include device-hoverable {
        .case-search__selector-btn:hover {
            color: var(--aqua-active);
            background-color: var(--base-dark-blue);
        }
    }
</style>

<style lang="scss">
    .case-search__dialog {
        overflow-y: hidden;
        max-width: 80% !important;
    }
</style>

<style scoped>
    #time-choose-menu >>> .v-icon,
    #time-choose-menu >>> .v-label,
    #time-choose-menu >>> .theme--light.v-input:not(.v-input--is-disabled) input,
    .theme--light.v-input:not(.v-input--is-disabled) textarea,
    #time-choose-menu >>> .v-select__selections,
    #time-choose-menu >>> .theme--light.v-input--is-disabled input,
    #time-choose-menu >>> .theme--light.v-select .v-select__selection--disabled {
        color: #fff;
    }

    @media (max-width: 1480px) {
        #time-choose-menu .history-search >>> .v-input__slot {
            width: 160px;
        }
    }

    @media (max-width: 1260px) {
        #time-choose-menu .history-search >>> .v-input__slot {
            width: 100%;
        }
    }

    #time-choose-menu >>> .v-input__slot::after {
        transform: none;
        border-color: #fff;
    }

    #time-choose-menu >>> .v-select__selection--comma {
        text-overflow: initial;
    }

    @media (min-width: 1600px) {
        #time-choose-menu >>> .v-select__selection--comma {
            font-size: 15px;
        }
    }

    #time-choose-menu >>> .v-treeview-node__children .v-treeview-node__root {
        max-height: initial;
    }

    #time-choose-menu >>> .v-treeview-node__content {
        align-items: flex-start;
    }

    #time-choose-menu >>> .checkbox-position {
        margin-top: 0;
    }

    #time-choose-menu >>> .v-treeview-node__label {
        overflow: visible;
        margin-top: 4px;
    }

    .cursor-auto {
        cursor: auto;
    }
</style>